import getConfig from 'next/config';

import {
    type DirectHotelOffer,
    type Filters,
    type Hotel,
    type HotelOffer,
    type HotelImage,
    type MapConfig,
    type SearchConfig,
    type SearchResultsState,
    KayakHotelProvider,
} from '@/interfaces';
import { SearchResultItemImages } from '../searchResultItemImages/SearchResultItemImages';
import { SearchResultItemDetails } from '../searchResultItemDetails/SearchResultItemDetails';
import { extractNameFromKey } from '@/utils/kayakKeys';
import { BestPriceBox } from '../bestPriceBox/BestPriceBox';
import { NoPricesBox } from '../noPricesBox/NoPricesBox';
import { filtersToHotelPageBrowserQueryParams } from '@/utils/url/hotelPageQueryParams';
import { pushUrl } from '@/utils/redirect';
import { useExtraHotelDataAPI } from '@/hooks/extraHotelDataAPI';
import { hasExtraHotelData } from '@/utils/integration/providers';
import { useUnleash } from '@/hooks/unleash';
import { mapDirectOffer, mapKayakOffer } from '@/utils/integration/hotelOfferMapper';
import { hasDirectMapping } from '@/utils/integration/providers';

const { publicRuntimeConfig } = getConfig();

export type SearchResultsItemProps = Pick<SearchResultsState, 'providers' | 'destination'> & {
    directPriceList?: readonly DirectHotelOffer[];
    directPriceIsLoading: boolean;
    filters: Filters;
    hotel: Hotel;
    mapOpen: boolean;
    openEditPanelWithFocus: () => void;
    searchConfig?: SearchConfig;
    setMapState: (config: MapConfig) => void;
};

export const SearchResultsItem = ({
    destination,
    directPriceList = [],
    directPriceIsLoading,
    filters,
    hotel,
    mapOpen,
    openEditPanelWithFocus,
    providers,
    searchConfig,
    setMapState,
}: SearchResultsItemProps) => {
    const unleash = useUnleash();
    const cleanHotelKey = extractNameFromKey(hotel.key);
    const country = destination?.hierarchy?.length ? extractNameFromKey(destination.hierarchy[0].key) : undefined;
    const extraHotelData = useExtraHotelDataAPI(cleanHotelKey ?? '');
    const numberOfRoomsSearched = filters.rooms ? filters.rooms.length : 1;
    const hcOffers = hotel.rates?.length
        ? hotel.rates
              .map((offer) => mapKayakOffer(offer, providers[offer.providerIndex], numberOfRoomsSearched))
              .filter((offer) => (hasDirectMapping(hotel.id, unleash) ? offer.provider.code !== KayakHotelProvider.REVATO : true))
        : [];
    const directOffers = directPriceList.map((offer) => mapDirectOffer(offer, unleash));
    const bestOffer: HotelOffer | undefined = [...directOffers, ...hcOffers].sort(
        (offer1, offer2) => offer1.totalRate + offer1.handicapRoomWeighting - (offer2.totalRate + offer2.handicapRoomWeighting),
    )[0];

    let place = extractNameFromKey(hotel.wellKnownPlace?.key);
    if (place === country) {
        place = extractNameFromKey(destination?.key);
    }
    const setSelectedHotel = () => {
        setMapState({ selectedHotel: { key: cleanHotelKey } });
    };
    const queryParams = filtersToHotelPageBrowserQueryParams(filters);
    const imageClick = () => {
        pushUrl(`${publicRuntimeConfig.basePath}/detaljer/${cleanHotelKey}/?${queryParams}`);
    };

    const hotelImages: readonly HotelImage[] = hasExtraHotelData(cleanHotelKey, unleash)
        ? (extraHotelData?.images?.map((image) => ({ large: image.url, caption: image.description })) ?? [])
        : hotel.images;
    const hotelName = hasExtraHotelData(cleanHotelKey, unleash) ? extraHotelData?.name : hotel.name;
    return (
        <article
            className="card result-list-item"
            data-testid={`searchResultsItem-${hotel.id}`}
            onMouseEnter={mapOpen ? setSelectedHotel : () => {}}
            aria-label={hotelName}>
            <SearchResultItemImages
                key={`${hotel.id}-${hotel.images?.length}`}
                id={hotel.id}
                images={hotelImages}
                name={hotelName}
                onClick={imageClick}
            />
            <SearchResultItemDetails
                id={hotel.id}
                hotelKey={hotel.key}
                name={hotelName}
                filters={filters}
                setMapState={setMapState}
                mapOpen={mapOpen}
                starRating={hotel.starRating}
                searchConfig={searchConfig}
                numberOfReviews={hotel.numberOfReviews}
                guestRating={hotel.guestRating}
            />
            {bestOffer ? (
                <BestPriceBox
                    offer={bestOffer}
                    hotelKey={hotel.key}
                    hotelId={hotel.id}
                    isLoading={directPriceIsLoading}
                    country={country}
                    place={place}
                    checkInDate={filters.checkInDate}
                    checkOutDate={filters.checkOutDate}
                    rooms={filters.rooms}
                />
            ) : (
                <NoPricesBox openEditPanelWithFocus={openEditPanelWithFocus} />
            )}
        </article>
    );
};
